import { ref } from 'vue'
import { useFormStore } from '@/stores/formStore'
import { useDataAttributes } from '@/composables/dataAttributes'
import { PrefillService } from '@/services/prefill.service'
import { createPartnerConnectService } from '@/services/api/partner-connect.service'
import { normalizePartnerConnectConfig } from '@/services/partner-connect-config.service'
import { defaultCalculationConfig } from '@/config/default.config'
import { installBootstrapMessages } from '@/plugins/i18n'
import { createPartnerContentSchema } from '@/services/partner-connect-content.service'
import type { ContentSchema } from '@/ts/types/schema.types'

interface ApplicationVariants {
  default: unknown
  mini: unknown
}

/**
 * Composable that handles the complete calculator initialization pipeline:
 * - Validates variant and partner ID
 * - Loads the Partner-Connect v2 integration including bootstrap (partner ID + integration ID)
 *   or the v1 compatibility config (partner ID only)
 * - Installs i18n messages
 * - Normalizes and applies configuration
 * - Applies prefill parameters
 *
 * @param applicationVariants - Mapping of variant names to Vue components
 * @returns Object with initialization state and the `initializeCalculator` function
 */
export const useCalculatorInitialization = (applicationVariants: ApplicationVariants) => {
  const isInitialized = ref(false)
  const initializationError = ref<Error | null>(null)
  const selectedVariant = ref<string>('default')

  const { fetchApplicationSchema } = useFormStore()
  const { variant, partnerId, integrationId } = useDataAttributes()

  const initializeCalculator = async () => {
    try {
      if (!partnerId) throw new Error('missing partner id')
      selectedVariant.value = variant ?? 'default'

      if (!(selectedVariant.value in applicationVariants)) throw new Error(`unknown variant: ${selectedVariant.value}`)

      const username = import.meta.env.VITE_PARTNER_CONNECT_BASIC_AUTH_USER
      const password = import.meta.env.VITE_PARTNER_CONNECT_BASIC_AUTH_PASSWORD
      const service = createPartnerConnectService(username && password ? { username, password } : undefined)
      let config = defaultCalculationConfig
      let contentSchema: ContentSchema | undefined

      try {
        const configuration = integrationId
          ? ((await service.getIntegration(partnerId, integrationId)).configuration ?? {})
          : await service.getLegacyConfig(partnerId)
        config = normalizePartnerConnectConfig(defaultCalculationConfig, configuration)
        if (integrationId) installBootstrapMessages(configuration.common?.bootstrap_leasingrechner?.i18n)
        try {
          contentSchema = createPartnerContentSchema(configuration.content)
        } catch (error) {
          console.warn('[Leasing Calculator]: invalid partner content, skipping content', error)
        }
      } catch (error) {
        console.warn('[Leasing Calculator]: Partner Connect unavailable, using default settings', error)
      }

      await fetchApplicationSchema(selectedVariant.value, config, contentSchema)
      await PrefillService.applyPrefillParams()

      isInitialized.value = true
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))
      initializationError.value = err
      console.error('[Leasing Calculator]: initialization failed', err)
    }
  }

  return {
    isInitialized,
    initializationError,
    selectedVariant,
    initializeCalculator
  }
}
